import React, { useState, useMemo, useRef, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, MarkerF, InfoWindowF } from '@react-google-maps/api';
import './MapView.css';

const containerStyle = {
  width: '100%',
  height: '100%'
};

const googleMapsApiKey = process.env.REACT_APP_FIREBASE_API_KEY;

const MapView = ({ events, onMarkerClick, mapState, onMapIdle }) => {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: googleMapsApiKey
  });

  const [activeLocation, setActiveLocation] = useState(null);

  const locations = useMemo(() => {
    const grouped = new Map();
    events.forEach(event => {
      if (event.latitude && event.longitude) {
        const key = `${event.latitude},${event.longitude}`;
        if (!grouped.has(key)) {
          grouped.set(key, {
            lat: parseFloat(event.latitude),
            lng: parseFloat(event.longitude),
            events: []
          });
        }
        grouped.get(key).events.push(event);
      }
    });
    return Array.from(grouped.values());
  }, [events]);

  const handleMarkerClick = (location) => {
    setActiveLocation(location);
  };
  
  const mapRef = useRef(null);
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  if (!isLoaded) return <div>Loading...</div>;

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={mapState.center}
      zoom={mapState.zoom}
      onLoad={onMapLoad}
      onIdle={() => onMapIdle(mapRef.current)}
      options={{ streetViewControl: false, mapTypeControl: false, zoomControl: true }}
      onClick={() => setActiveLocation(null)}
    >
      {locations.map((location) => {
        const primaryColor = location.events[0].category_details?.color || '#1976d2';
        
        return (
          <MarkerF
            key={`${location.lat},${location.lng}`}
            position={{ lat: location.lat, lng: location.lng }}
            onClick={() => handleMarkerClick(location)}
            label={location.events.length > 1 ? { text: String(location.events.length), color: '#ffffff' } : undefined}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              fillColor: primaryColor,
              fillOpacity: 0.9,
              scale: location.events.length > 1 ? 12 : 8,
              strokeColor: '#FFFFFF',
              strokeWeight: 2,
            }}
          />
        );
      })}

      {activeLocation && (
        <InfoWindowF
          position={{ lat: activeLocation.lat, lng: activeLocation.lng }}
          onCloseClick={() => setActiveLocation(null)}
        >
          <div className="infowindow-container">
            <h4>Events in {activeLocation.events[0].city}</h4>
            <ul className="infowindow-list" style={{ listStyleType: 'none', padding: 0 }}>
              {activeLocation.events.map(event => (
                <li 
                  key={event.id} 
                  onClick={() => onMarkerClick(event)}
                  style={{ cursor: 'pointer', marginBottom: '8px', borderBottom: '1px solid #eee', paddingBottom: '4px' }}
                >
                  <strong>{event.title}</strong>
                  <br />
                  <span style={{ fontSize: '0.8rem', color: event.category_details?.color || '#666' }}>
                    {event.category_details?.icon} {event.category}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </InfoWindowF>
      )}
    </GoogleMap>
  );
};

export default React.memo(MapView);